import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Text,
} from '@chakra-ui/react'
import { FiEdit, FiPrinter, FiRefreshCcw, FiX } from 'react-icons/fi'
import {
  CreatePrescriptionResponse,
  CreatePrescriptionRequest,
} from '../../../services/prescriptions'
import { BatchRequestState } from '../../../hooks/useBatchMutation'
import { PrescribedItem, Prescription } from '../../../types'
import { useAppDispatch, useAppSelector } from '../../../app/typedReduxHooks'
import { printPaperPrescription } from './PaperPrescription'
import { printEvidenceOfPrescription } from '../../PrescriptionsPage/components/EvidenceOfPrescription'
import {
  cleanPrescribedItems,
  resetPrescribedItemByIndex,
  resetScript,
  setDeliveryState,
} from '../ScriptForm/PrescribedItem/PrescribedItemSlice'
import { blankPatient, selectPatient } from '../../../features/patient/PatientContextSlice'
import ClientSSOContext from '../../../features/sso/types/ClientSSOContext'
import { useEffect, useState } from 'react'
import { PrescriptionDeliveryMethod } from '../../../types/prescription-delivery-method'

interface SendScriptResultsModalProps {
  isOpen: boolean
  onClose: () => void
  states: Record<string, BatchRequestState<CreatePrescriptionRequest, CreatePrescriptionResponse>>
  prescribedItems: PrescribedItem[]
  prescriptionsNumber: {
    success: number
    failed: number
  }
  onAbort: () => void
  isLoading: boolean
  prescriptions: Prescription[]
  onResendToken: (isOpen: boolean) => void
  onEditFailed: () => void
}

const SendScriptResultsModal = ({
  isOpen,
  onClose,
  onEditFailed,
  states,
  prescribedItems,
  prescriptionsNumber,
  onAbort,
  isLoading,
  prescriptions,
  onResendToken,
}: SendScriptResultsModalProps) => {
  const { isPaperPrescription } = useAppSelector((state) => state.newScript)
  const dispatch = useAppDispatch()
  const organization = useAppSelector((state) => state.organization?.data)
  const [clientSSOContextData, setClientSSOContextData] = useState<ClientSSOContext | null>(null)
  const defaultPrescriptionDeliveryMethod =
    organization?.settings?.defaultPrescriptionDeliveryMethod
  const selectedPatient = useAppSelector((state) => state.patientDetail.selectedPatient)

  useEffect(() => {
    loadClientSSOContextData()
  }, [])

  const loadClientSSOContextData = () => {
    try {
      const clientSSOContextDataString = localStorage.getItem('clientSSOContext')
      const clientSSOContextData: ClientSSOContext | null = clientSSOContextDataString
        ? JSON.parse(clientSSOContextDataString)
        : null
      setClientSSOContextData(clientSSOContextData)
    } catch (error) {
      console.error('Failed to parse client SSO context:', error)
      setClientSSOContextData(null)
    }
  }

  const handleEditFailed = () => {
    // Reset all successfully created prescriptions
    prescribedItems.forEach((item, index) => {
      const state = states[`${index}-${item.productId}`]
      if (state?.data?.eRxPrescription?.status === 'Success' && state.data?.prescription?.scid) {
        dispatch(resetPrescribedItemByIndex(index))
      }
    })
    // Clean up empty items
    dispatch(cleanPrescribedItems())
    onEditFailed()
  }

  const handleCloseModal = () => {
    dispatch(resetScript())
    if (organization?.settings?.enableManagePatient) {
      if (!clientSSOContextData?.externalPatientFetchId) {
        dispatch(selectPatient(blankPatient))
      }
    } else {
      dispatch(
        setDeliveryState({
          sendViaEmail: defaultPrescriptionDeliveryMethod === PrescriptionDeliveryMethod.Email,
          sendViaSMS: defaultPrescriptionDeliveryMethod === PrescriptionDeliveryMethod.SMS,
          printPaperToken:
            defaultPrescriptionDeliveryMethod === PrescriptionDeliveryMethod.Paper_Token,
          recipientEmail: selectedPatient?.email,
          recipientMobile: selectedPatient?.phone_number,
        })
      )
    }
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Text>Send Script Results</Text>
          <Text fontSize="sm" color="gray.500" fontWeight="normal">
            {prescriptionsNumber.success} successful • {prescriptionsNumber.failed} failed
          </Text>
        </ModalHeader>
        <ModalBody borderTop="1px solid" borderColor="gray.200" p="0">
          <SimpleGrid columns={1}>
            {prescribedItems.map((item, index) => {
              const state = states[`${index}-${item.productId}`]

              // Extract error message from various possible sources
              const getErrorMessage = (): string | undefined => {
                if (state?.error instanceof Error) {
                  return state.error.message
                }
                if (state?.error && typeof state.error === 'string') {
                  return state.error
                }
                if (
                  state?.data?.eRxPrescription?.status !== 'Success' &&
                  state?.data &&
                  typeof state.data === 'object'
                ) {
                  const data = state.data as any
                  return typeof data.message === 'string' ? data.message : JSON.stringify(data)
                }
                return undefined
              }

              const errorMessage = getErrorMessage()

              return (
                <>
                  <Flex
                    justifyContent="space-between"
                    alignItems="center"
                    key={`${item.productId}-${index}`}
                    p={4}
                  >
                    <Text>{item.displayName}</Text>

                    {state?.loading ? (
                      <Spinner size="sm" />
                    ) : state?.data?.eRxPrescription?.status === 'Success' &&
                      state?.data?.prescription?.scid ? (
                      <Badge colorScheme="green" textTransform="none">
                        Sent
                      </Badge>
                    ) : (
                      <Badge colorScheme="red" textTransform="none">
                        Failed
                      </Badge>
                    )}
                  </Flex>

                  {errorMessage && (
                    <Box p={4} pt="0">
                      <Text color="red.500">{errorMessage}</Text>
                    </Box>
                  )}
                  {index <= prescribedItems.length - 1 && <Divider />}
                </>
              )
            })}
          </SimpleGrid>
        </ModalBody>
        <ModalFooter>
          <Flex
            gap={2}
            alignItems="center"
            justifyContent="flex-start"
            fontSize="sm"
            color="gray.500"
            flex={1}
          >
            {isLoading && (
              <Button
                colorScheme="red"
                onClick={() => {
                  onAbort()
                }}
                leftIcon={<FiX />}
              >
                Abort
              </Button>
            )}

            {prescriptionsNumber.success > 0 && (
              <>
                <Text fontWeight="bold">{prescriptionsNumber.success} SENT:</Text>
                <Button
                  onClick={() =>
                    isPaperPrescription
                      ? printPaperPrescription(prescriptions)
                      : printEvidenceOfPrescription(prescriptions)
                  }
                  leftIcon={<FiPrinter />}
                  color="gray.600"
                >
                  Print
                </Button>
                {!isPaperPrescription && (
                  <Button
                    onClick={() => {
                      onResendToken(true)
                    }}
                    leftIcon={<FiRefreshCcw />}
                    color="gray.600"
                  >
                    Resend Token
                  </Button>
                )}
              </>
            )}
            {prescriptionsNumber.failed > 0 && (
              <>
                <Text color="red.500" fontWeight="bold">
                  {prescriptionsNumber.failed} FAILED:
                </Text>
                <Button onClick={handleEditFailed} leftIcon={<FiEdit />} color="gray.600">
                  Edit Failed
                </Button>
              </>
            )}
          </Flex>
          <Button colorScheme="blue" onClick={handleCloseModal}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default SendScriptResultsModal
